import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useLang } from '../../contexts/LangContext';
import AlunaTabBar from '../../components/AlunaTabBar';
import EvolucaoLineChart from '../../components/EvolucaoLineChart';
import { temConteudo } from '../../lib/avaliacaoDetalhes';

const T = {
  pt: {
    title: 'Minha Evolução',
    subtitle: 'Acompanhe seu progresso ao longo do tempo',
    noData: 'Nenhum registro ainda.',
    noDataSub: 'Aguarde o registro da sua personal após a avaliação física.',
    dados: 'Dados',
    peso: 'Peso', gordura: '% Gordura', cintura: 'Cintura',
    abdomen: 'Abdômen', quadril: 'Quadril', braco: 'Braço',
    coxa: 'Coxa', panturrilha: 'Panturrilha',
    bmi: 'BMI', pesoSemGordura: 'Peso sem gordura', gorduraSubcutanea: 'Gordura subcutânea',
    gorduraVisceral: 'Gordura visceral', liquidoCorporal: 'Líquido corporal',
    musculoEsqueletico: 'Músculo esquelético', massaMuscular: 'Massa muscular',
    massaOssea: 'Massa óssea', proteina: 'Proteína', bmr: 'BMR', idadeMetabolica: 'Idade metabólica',
    unidadeKg: 'kg', unidadeCm: 'cm', unidadePct: '%', unidadeKcal: 'kcal', unidadeAnos: 'anos',
    historico: 'Histórico de Registros',
    grafico: 'Gráfico de Evolução',
    registros: 'registros',
    equipe: 'equipe',
    resumoTitle: 'Resumo do seu acompanhamento',
    resumoUpdated: 'Revisado pela equipe',
  },
  en: {
    title: 'My Progress', subtitle: 'Track your progress over time',
    noData: 'No records yet.',
    noDataSub: 'Wait for your trainer to record your measurements after your assessment.',
    dados: 'Data',
    peso: 'Weight', gordura: 'Body Fat', cintura: 'Waist',
    abdomen: 'Abdomen', quadril: 'Hips', braco: 'Arm', coxa: 'Thigh', panturrilha: 'Calf',
    bmi: 'BMI', pesoSemGordura: 'Fat-free weight', gorduraSubcutanea: 'Subcutaneous fat',
    gorduraVisceral: 'Visceral fat', liquidoCorporal: 'Body water',
    musculoEsqueletico: 'Skeletal muscle', massaMuscular: 'Muscle mass',
    massaOssea: 'Bone mass', proteina: 'Protein', bmr: 'BMR', idadeMetabolica: 'Metabolic age',
    unidadeKg: 'kg', unidadeCm: 'cm', unidadePct: '%', unidadeKcal: 'kcal', unidadeAnos: 'yrs',
    historico: 'Record History', grafico: 'Progress Chart', registros: 'records',
    equipe: 'team',
    resumoTitle: 'Your progress summary',
    resumoUpdated: 'Reviewed by the team',
  },
};

const INDICADORES = (t) => [
  { key: 'peso',                 label: t.peso,                unidade: t.unidadeKg,  cor: '#5B402E' },
  { key: 'gordura',               label: t.gordura,             unidade: t.unidadePct, cor: '#C0845A' },
  { key: 'cintura',               label: t.cintura,             unidade: t.unidadeCm,  cor: '#432F21' },
  { key: 'abdomen',               label: t.abdomen,             unidade: t.unidadeCm,  cor: '#8B7355' },
  { key: 'quadril',               label: t.quadril,             unidade: t.unidadeCm,  cor: '#6B8E6B' },
  { key: 'braco',                 label: t.braco,               unidade: t.unidadeCm,  cor: '#7B9E87' },
  { key: 'coxa',                  label: t.coxa,                unidade: t.unidadeCm,  cor: '#5E7A6E' },
  { key: 'panturrilha',           label: t.panturrilha,         unidade: t.unidadeCm,  cor: '#4E6E6B' },
  { key: 'bmi',                   label: t.bmi,                 unidade: '',           cor: '#8C6A4F' },
  { key: 'peso_sem_gordura',      label: t.pesoSemGordura,      unidade: t.unidadeKg,  cor: '#5B402E' },
  { key: 'gordura_subcutanea',    label: t.gorduraSubcutanea,   unidade: t.unidadePct, cor: '#C0845A' },
  { key: 'gordura_visceral',      label: t.gorduraVisceral,     unidade: '',           cor: '#A8724F' },
  { key: 'liquido_corporal',      label: t.liquidoCorporal,     unidade: t.unidadePct, cor: '#5E7A6E' },
  { key: 'musculo_esqueletico',   label: t.musculoEsqueletico,  unidade: t.unidadePct, cor: '#6B8E6B' },
  { key: 'massa_muscular',        label: t.massaMuscular,       unidade: t.unidadeKg,  cor: '#4E6E6B' },
  { key: 'massa_ossea',           label: t.massaOssea,          unidade: t.unidadeKg,  cor: '#432F21' },
  { key: 'proteina',              label: t.proteina,            unidade: t.unidadePct, cor: '#7B9E87' },
  { key: 'bmr',                   label: t.bmr,                 unidade: t.unidadeKcal, cor: '#8B7355' },
  { key: 'idade_metabolica',      label: t.idadeMetabolica,     unidade: t.unidadeAnos, cor: '#5B402E' },
];

const SECOES_EXTRAS = [
  {
    key: 'identificacao', titulo: 'Objetivo e planejamento',
    campos: [
      ['altura', 'Altura'], ['objetivo', 'Objetivo principal'], ['regiao_melhorar', 'Região a melhorar'],
      ['proxima_avaliacao', 'Próxima avaliação', 'data'],
    ],
  },
  {
    key: 'anamnese', titulo: 'Anamnese',
    campos: [
      ['sono', 'Sono'], ['hidratacao', 'Hidratação'], ['alimentacao', 'Alimentação'],
      ['frequencia_treino', 'Frequência de treino'], ['historico_lesoes', 'Lesões e desconfortos'],
      ['medicamentos', 'Medicamentos'],
    ],
  },
  {
    key: 'perimetria', titulo: 'Perimetria completa',
    campos: [
      ['pescoco', 'Pescoço', 'cm'], ['torax', 'Tórax', 'cm'], ['abdomen_alto', 'Abdômen alto', 'cm'],
      ['cintura', 'Cintura', 'cm'], ['abdomen_baixo', 'Abdômen baixo', 'cm'],
      ['biceps_direito', 'Bíceps direito', 'cm'], ['biceps_esquerdo', 'Bíceps esquerdo', 'cm'],
      ['quadril', 'Quadril', 'cm'], ['coxa_direita', 'Coxa direita', 'cm'], ['coxa_esquerda', 'Coxa esquerda', 'cm'],
      ['panturrilha_direita', 'Panturrilha direita', 'cm'], ['panturrilha_esquerda', 'Panturrilha esquerda', 'cm'],
    ],
  },
  {
    key: 'dobras', titulo: 'Dobras cutâneas',
    campos: [
      ['subescapular', 'Subescapular', 'mm'], ['triceps', 'Tríceps', 'mm'], ['peitoral', 'Peitoral', 'mm'],
      ['axilar_media', 'Axilar média', 'mm'], ['supra_iliaca', 'Supra-ilíaca', 'mm'],
      ['abdomen', 'Abdômen', 'mm'], ['coxa', 'Coxa', 'mm'], ['somatoria', 'Somatória das 7 dobras', 'mm'],
    ],
  },
  {
    key: 'postura', titulo: 'Avaliação postural',
    campos: [
      ['ombros', 'Ombros'], ['trapezio', 'Trapézio'], ['joelhos', 'Joelhos'],
      ['quadril', 'Quadril'], ['coluna', 'Coluna'], ['pes', 'Pés'],
    ],
  },
  {
    key: 'testes', titulo: 'Avaliação funcional e testes',
    campos: [
      ['flexibilidade', 'Flexibilidade — Banco de Wells'], ['flexao_braco', 'Flexão de braço'],
      ['abdominal', 'Abdominal'], ['observacoes', 'Observações funcionais'],
    ],
  },
  {
    key: 'sintese', titulo: 'Síntese da avaliação',
    campos: [
      ['bioimpedancia', 'Bioimpedância'], ['perimetria', 'Perimetria'], ['dobras', 'Dobras cutâneas'],
      ['impressao_clinica', 'Impressão clínica'],
    ],
  },
];

function formatarValorExtra(valor, unidade, locale) {
  if (unidade === 'data') {
    const data = new Date(`${valor}T12:00:00`);
    return Number.isNaN(data.getTime()) ? valor : data.toLocaleDateString(locale);
  }
  const numero = Number(String(valor).replace(',', '.'));
  if (unidade && Number.isFinite(numero)) {
    return `${numero.toLocaleString(locale, { maximumFractionDigits: 2 })} ${unidade}`;
  }
  return String(valor);
}

// ─── Página principal (somente leitura — quem lança as medidas é o admin) ────
export default function AlunaEvolucao() {
  const { user } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();
  const t = T[lang] || T.pt;
  const indicadores = INDICADORES(t);

  const [registros, setRegistros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [indicadorAtivo, setIndicadorAtivo] = useState('peso');
  const [resumoAprovado, setResumoAprovado] = useState(null);
  const [resumoExpandido, setResumoExpandido] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    setLoading(true);
    Promise.all([
      supabase.from('evolucao_fisica').select('*').eq('aluna_id', user.id).order('data', { ascending: true }),
      supabase.from('perfis').select('anamnese').eq('aluna_id', user.id).maybeSingle(),
    ]).then(([evolucao, perfil]) => {
      setRegistros(evolucao.data || []);
      const resumo = perfil.data?.anamnese?._resumoProntuario;
      setResumoAprovado(resumo?.alunoAprovado ? { texto: resumo.alunoAprovado, data: resumo.aprovadoEm } : null);
      setLoading(false);
    });
  }, [user]);

  const indConfig = indicadores.find(i => i.key === indicadorAtivo);
  const ultimoRegistro = registros.length > 0 ? registros[registros.length - 1] : null;
  const indicadoresComDado = indicadores.filter(ind => ultimoRegistro?.[ind.key] != null);
  const locale = lang === 'pt' ? 'pt-BR' : 'en-US';
  const fmtDate = str => new Date(str + 'T12:00:00').toLocaleDateString(locale, { day:'2-digit', month:'2-digit', year:'numeric' });
  const fmtDateLonga = str => new Date(str + 'T12:00:00').toLocaleDateString(locale, { day:'numeric', month:'short', year:'numeric' });
  const formatarIndicador = (registro, indicador) => {
    const numero = Number(registro?.[indicador.key]);
    if (!Number.isFinite(numero)) return '—';
    return numero.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 2 });
  };
  const indicadoresDestaque = indicadoresComDado.filter(ind => ['peso', 'gordura', 'cintura'].includes(ind.key));
  const medidasCorporais = indicadoresComDado.filter(ind => ['abdomen', 'quadril', 'braco', 'coxa', 'panturrilha'].includes(ind.key));
  const bioimpedancia = indicadoresComDado.filter(ind => !['peso', 'gordura', 'cintura', 'abdomen', 'quadril', 'braco', 'coxa', 'panturrilha'].includes(ind.key));
  const resumoParagrafos = String(resumoAprovado?.texto || '').split(/\n\s*\n/).map(p => p.trim()).filter(Boolean);
  const resumoLongo = resumoParagrafos.length > 1 || String(resumoAprovado?.texto || '').length > 260;
  const primeiroParagrafo = resumoParagrafos[0] || '';
  const resumoPrevia = primeiroParagrafo.length > 240 ? `${primeiroParagrafo.slice(0, 237).trim()}...` : primeiroParagrafo;

  return (
    <div style={{ minHeight:'100vh', background:'#f3eee7', padding:'26px 14px 40px', fontFamily:"-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', 'Inter', sans-serif" }}>
      <nav style={{ width:'min(100%, 430px)', margin:'0 auto', background:'#fffaf4', borderRadius:'44px 44px 0 0', padding:'28px 22px 0', display:'flex', alignItems:'center', justifyContent:'space-between', position:'relative', zIndex:50, boxShadow:'0 0 0 1px rgba(255,255,255,.72), 0 28px 80px rgba(66,44,38,.10)' }}>
        <img src="/logo.svg" alt="Team Carol Macedo" style={{ height:'30px', width:'auto', objectFit:'contain' }} />
        <button onClick={() => navigate('/aluna/hoje')} style={{ height:'42px', padding:'0 16px', background:'rgba(255,255,255,.56)', border:'1px solid rgba(216,202,182,.86)', color:'#7f7165', borderRadius:'999px', fontSize:'13px', cursor:'pointer' }}>{lang === 'pt' ? 'Voltar' : 'Back'}</button>
      </nav>
      <div style={{ width:'min(100%, 430px)', minHeight:'calc(100vh - 56px)', margin:'0 auto', padding:'24px 22px 118px', background:'#fffaf4', borderRadius:'0 0 44px 44px', boxShadow:'0 28px 80px rgba(66,44,38,.10), inset 0 -1px 0 rgba(255,255,255,.95)' }}>
      <style>{`
        .evo-summary-card {
          position: relative;
          overflow: hidden;
          background: linear-gradient(145deg, #f1f2df 0%, #fbf8ee 58%, #fff 100%);
          border: 1px solid rgba(185, 176, 139, .45);
          border-radius: 28px;
          padding: 20px;
          margin-bottom: 18px;
          box-shadow: 0 14px 34px rgba(82,69,57,.065);
        }
        .evo-summary-card::before {
          content: "";
          position: absolute;
          width: 110px;
          height: 110px;
          border-radius: 50%;
          right: -45px;
          top: -50px;
          background: rgba(168, 178, 112, .13);
        }
        .evo-summary-heading {
          position: relative;
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 12px;
        }
        .evo-summary-icon {
          width: 34px;
          height: 34px;
          border-radius: 12px;
          display: grid;
          place-items: center;
          color: #667044;
          background: rgba(255,255,255,.68);
          border: 1px solid rgba(140,151,89,.18);
          font-size: 16px;
        }
        .evo-summary-copy {
          color: #4c493f;
          font-size: 13px;
          line-height: 1.68;
          white-space: pre-wrap;
          margin: 0;
        }
        .evo-summary-copy + .evo-summary-copy { margin-top: 13px; }
        .evo-summary-toggle {
          margin-top: 14px;
          padding: 8px 13px;
          border: 1px solid rgba(102,112,68,.2);
          border-radius: 999px;
          background: rgba(255,255,255,.64);
          color: #626b45;
          font-size: 11px;
          font-weight: 700;
          cursor: pointer;
        }
        .evo-assessment-card {
          background: rgba(255,255,255,.82);
          border: 1px solid rgba(216,202,182,.72);
          border-radius: 28px;
          overflow: hidden;
          margin-bottom: 18px;
          box-shadow: 0 12px 28px rgba(82,69,57,.055);
        }
        .evo-assessment-header {
          padding: 18px 18px 13px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }
        .evo-assessment-kicker {
          color: #9b8978;
          font-size: 10px;
          font-weight: 750;
          text-transform: uppercase;
          letter-spacing: .08em;
          margin-bottom: 3px;
        }
        .evo-assessment-date { color: #2d2420; font-size: 18px; font-weight: 680; }
        .evo-assessment-badge {
          background: #5b402e;
          color: #fff8f1;
          padding: 7px 11px;
          border-radius: 999px;
          font-size: 10px;
          font-weight: 700;
        }
        .evo-highlight-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 8px;
          padding: 0 14px 14px;
        }
        .evo-highlight {
          min-width: 0;
          border: 1px solid #ede3d6;
          border-radius: 18px;
          padding: 12px 9px;
          background: #fbf8f3;
          text-align: left;
          cursor: pointer;
        }
        .evo-highlight.active {
          border-color: rgba(91,64,46,.36);
          background: #f3ebe3;
          box-shadow: inset 0 0 0 1px rgba(91,64,46,.06);
        }
        .evo-highlight-label {
          display: block;
          color: #907f70;
          font-size: 9px;
          font-weight: 750;
          text-transform: uppercase;
          letter-spacing: .055em;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          margin-bottom: 5px;
        }
        .evo-highlight-value { color: #2b221f; font-size: 17px; font-weight: 720; }
        .evo-highlight-unit { color: #a38d75; font-size: 9px; font-weight: 650; margin-left: 2px; }
        .evo-metric-section { border-top: 1px solid #eee5da; }
        .evo-metric-section > summary {
          list-style: none;
          cursor: pointer;
          padding: 15px 18px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          color: #4a372d;
          font-size: 13px;
          font-weight: 680;
        }
        .evo-metric-section > summary::-webkit-details-marker { display: none; }
        .evo-metric-section > summary small { color: #a18f7e; font-size: 10px; font-weight: 600; }
        .evo-metric-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 8px;
          padding: 0 14px 14px;
        }
        .evo-metric {
          min-width: 0;
          border: 1px solid #eee6dc;
          border-radius: 16px;
          background: #fffdf9;
          padding: 11px 12px;
          text-align: left;
          cursor: pointer;
        }
        .evo-metric.active { background: #f3ebe3; border-color: rgba(91,64,46,.28); }
        .evo-metric-label {
          display: block;
          color: #75675c;
          font-size: 10px;
          line-height: 1.25;
          margin-bottom: 5px;
        }
        .evo-metric-value { color: #2b221f; font-size: 15px; font-weight: 700; }
      `}</style>
      <div style={{ marginBottom:'28px' }}>
        <h1 style={{ fontSize:'30px', color:'#251c19', margin:0, fontWeight:650, letterSpacing:0 }}>{t.title}</h1>
        <p style={{ color:'#8B7355', margin:'4px 0 0', fontSize:'14px' }}>{t.subtitle}</p>
      </div>

      {resumoAprovado && (
        <section className="evo-summary-card">
          <div className="evo-summary-heading">
            <div className="evo-summary-icon" aria-hidden="true">✦</div>
            <div>
              <div style={{ color:'#5e6840', fontSize:'10px', fontWeight:750, letterSpacing:'.08em', textTransform:'uppercase' }}>{t.resumoTitle}</div>
              <div style={{ color:'#898b6e', fontSize:'10px', marginTop:'2px' }}>{t.resumoUpdated}</div>
            </div>
          </div>
          {(resumoExpandido ? resumoParagrafos : [resumoPrevia]).map((paragrafo, i) => (
            <p className="evo-summary-copy" key={i}>{paragrafo}</p>
          ))}
          {resumoLongo && (
            <button className="evo-summary-toggle" type="button" onClick={() => setResumoExpandido(v => !v)}>
              {resumoExpandido ? 'Recolher resumo' : 'Ler resumo completo'}
            </button>
          )}
          <small style={{ display:'block', color:'#8d8f74', fontSize:'9px', marginTop:'12px' }}>
            {t.resumoUpdated}{resumoAprovado.data ? ` · ${new Date(resumoAprovado.data).toLocaleDateString(locale)}` : ''}
          </small>
        </section>
      )}

      {loading ? (
        <div style={{ textAlign:'center', padding:'60px 0', color:'#8B7355' }}>
          <div style={{ width:'36px', height:'36px', border:'3px solid #E8E0D0', borderTopColor:'#5B402E', borderRadius:'50%', animation:'spin 0.8s linear infinite', margin:'0 auto 16px' }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      ) : registros.length === 0 ? (
        <div style={{ background:'rgba(255,255,255,.78)', border:'1px solid rgba(216,202,182,.72)', borderRadius:'28px', padding:'48px 28px', textAlign:'center', boxShadow:'0 12px 28px rgba(82,69,57,.055), inset 0 1px 0 rgba(255,255,255,.84)' }}>
          <div style={{ width:'58px', height:'58px', borderRadius:'22px', background:'#e8d5c7', color:'#895b40', display:'grid', placeItems:'center', margin:'0 auto 16px' }}>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 3v18h18" />
              <path d="M7 15l4-4 3 3 5-6" />
            </svg>
          </div>
          <div style={{ fontSize:'17px', fontWeight:600, color:'#251C19', marginBottom:'8px' }}>{t.noData}</div>
          <div style={{ fontSize:'14px', color:'#8B7355', maxWidth:'300px', margin:'0 auto' }}>{t.noDataSub}</div>
        </div>
      ) : (
        <>
          {/* Última medição — resumo em cartões */}
          <div className="evo-assessment-card">
            <div className="evo-assessment-header">
              <div>
                <div className="evo-assessment-kicker">Última avaliação</div>
                <div className="evo-assessment-date">{fmtDateLonga(ultimoRegistro.data)}</div>
              </div>
              <span className="evo-assessment-badge">{indicadoresComDado.length} indicadores</span>
            </div>
            <div className="evo-highlight-grid">
              {indicadoresDestaque.map(ind => (
                <button key={ind.key} type="button" className={`evo-highlight ${indicadorAtivo === ind.key ? 'active' : ''}`} onClick={() => setIndicadorAtivo(ind.key)}>
                  <span className="evo-highlight-label">{ind.label}</span>
                  <span className="evo-highlight-value">{formatarIndicador(ultimoRegistro, ind)}<small className="evo-highlight-unit">{ind.unidade}</small></span>
                </button>
              ))}
            </div>
            {medidasCorporais.length > 0 && (
              <details className="evo-metric-section">
                <summary><span>Medidas corporais</span><small>{medidasCorporais.length} medidas · abrir</small></summary>
                <div className="evo-metric-grid">
                  {medidasCorporais.map(ind => (
                    <button key={ind.key} type="button" className={`evo-metric ${indicadorAtivo === ind.key ? 'active' : ''}`} onClick={() => setIndicadorAtivo(ind.key)}>
                      <span className="evo-metric-label">{ind.label}</span>
                      <span className="evo-metric-value">{formatarIndicador(ultimoRegistro, ind)}<small className="evo-highlight-unit">{ind.unidade}</small></span>
                    </button>
                  ))}
                </div>
              </details>
            )}
            {bioimpedancia.length > 0 && (
              <details className="evo-metric-section">
                <summary><span>Bioimpedância</span><small>{bioimpedancia.length} indicadores · abrir</small></summary>
                <div className="evo-metric-grid">
                  {bioimpedancia.map(ind => (
                    <button key={ind.key} type="button" className={`evo-metric ${indicadorAtivo === ind.key ? 'active' : ''}`} onClick={() => setIndicadorAtivo(ind.key)}>
                      <span className="evo-metric-label">{ind.label}</span>
                      <span className="evo-metric-value">{formatarIndicador(ultimoRegistro, ind)}<small className="evo-highlight-unit">{ind.unidade}</small></span>
                    </button>
                  ))}
                </div>
              </details>
            )}
          </div>

          {temConteudo(ultimoRegistro.detalhes) && (
            <section style={{ marginBottom:'18px' }}>
              <div style={{ fontSize:'11px', fontWeight:700, color:'#8B7355', letterSpacing:'.08em', textTransform:'uppercase', margin:'0 4px 10px' }}>
                Avaliação completa
              </div>
              {SECOES_EXTRAS.map(secao => {
                const dados = ultimoRegistro.detalhes?.[secao.key];
                if (!temConteudo(dados)) return null;
                return (
                  <details key={secao.key} style={{ background:'rgba(255,255,255,.78)', border:'1px solid rgba(216,202,182,.72)', borderRadius:'22px', overflow:'hidden', marginBottom:'9px', boxShadow:'0 8px 20px rgba(82,69,57,.04)' }}>
                    <summary style={{ cursor:'pointer', listStyle:'none', padding:'16px 18px', fontSize:'14px', fontWeight:650, color:'#251C19', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                      {secao.titulo}
                      <span aria-hidden="true" style={{ color:'#A8937A', fontSize:'17px' }}>⌄</span>
                    </summary>
                    <div style={{ borderTop:'1px solid #EEE5D8', padding:'4px 18px 14px' }}>
                      {secao.campos.map(([key, label, unidade]) => temConteudo(dados[key]) && (
                        <div key={key} style={{ padding:'11px 0', borderBottom:'1px solid #F0EBE0' }}>
                          <div style={{ color:'#9A8978', fontSize:'10px', fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:'4px' }}>{label}</div>
                          <div style={{ color:'#3F332D', fontSize:'13px', lineHeight:1.55, whiteSpace:'pre-wrap' }}>{formatarValorExtra(dados[key], unidade, locale)}</div>
                        </div>
                      ))}
                    </div>
                  </details>
                );
              })}
            </section>
          )}

          {/* Gráfico */}
          <div style={{ background:'rgba(255,255,255,.78)', border:'1px solid rgba(216,202,182,.72)', borderRadius:'28px', padding:'20px 16px 12px', marginBottom:'18px', boxShadow:'0 12px 28px rgba(82,69,57,.055), inset 0 1px 0 rgba(255,255,255,.84)' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'16px', flexWrap:'wrap', gap:'8px' }}>
              <div>
                <div style={{ fontSize:'13px', color:'#8B7355', marginBottom:'2px' }}>{t.grafico}</div>
                <div style={{ fontSize:'15px', fontWeight:600, color:'#251C19', display:'flex', alignItems:'center', gap:'8px' }}>
                  <span style={{ display:'inline-block', width:'10px', height:'10px', borderRadius:'50%', background:indConfig?.cor }} />
                  {indConfig?.label}
                </div>
              </div>
              <div style={{ fontSize:'12px', color:'#8B7355' }}>{registros.length} {t.registros}</div>
            </div>
            <EvolucaoLineChart dados={registros} indicador={indicadorAtivo} cor={indConfig?.cor||'#5B402E'} unidade={indConfig?.unidade||''} />
          </div>

          {/* Histórico */}
          <div style={{ background:'rgba(255,255,255,.78)', border:'1px solid rgba(216,202,182,.72)', borderRadius:'28px', overflow:'hidden', boxShadow:'0 12px 28px rgba(82,69,57,.055), inset 0 1px 0 rgba(255,255,255,.84)' }}>
            <div style={{ padding:'16px 20px', borderBottom:'1px solid #E8E0D0', fontSize:'15px', fontWeight:600, color:'#251C19' }}>{t.historico}</div>
            {[...registros].reverse().map((r) => (
              <div key={r.id} style={{ padding:'14px 20px', borderBottom:'1px solid #F0EBE0' }}>
                <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'6px' }}>
                  <span style={{ fontSize:'13px', fontWeight:600, color:'#251C19' }}>{fmtDate(r.data)}</span>
                  <span style={{ fontSize:'10px', padding:'2px 7px', borderRadius:'100px', background:'rgba(46,125,50,0.08)', color:'#2E7D32', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.04em' }}>
                    {t.equipe}
                  </span>
                </div>
                <div style={{ display:'flex', flexWrap:'wrap', gap:'8px' }}>
                  {indicadores.map(ind => r[ind.key] != null && (
                    <span key={ind.key} style={{ fontSize:'12px', color: indicadorAtivo===ind.key?ind.cor:'#4A3728', fontWeight: indicadorAtivo===ind.key?700:400 }}>
                      {ind.label}: {parseFloat(r[ind.key]).toFixed(1)}{ind.unidade}
                    </span>
                  ))}
                </div>
                {r.observacoes && <div style={{ fontSize:'11px', color:'#A8937A', marginTop:'4px', fontStyle:'italic' }}>{r.observacoes}</div>}
              </div>
            ))}
          </div>
        </>
      )}
      </div>
      <AlunaTabBar current="saude" />
    </div>
  );
}
